import React, { useEffect, useState } from "react";
import { Row, Col, Table } from "react-bootstrap";

import IconButton from "components/icon-button";
import BarChart from "./bar-chart";
import queryBillingData from "services/queryBillingData";

import { ReactComponent as BrightGreenRectangle } from "assets/svg/bright-green-rectangle.svg";
import { ReactComponent as BlueRectangle } from "assets/svg/blue-rectangle.svg";
import { ReactComponent as Previous } from "assets/svg/outline-chevron-left-16.svg";
import { ReactComponent as Next } from "assets/svg/outline-chevron-right-16.svg";
import styles from "./styles.module.scss";
import { AmplitudePageName, track } from "utils/amplitude";

// TODO: use UTC for now
const nowYear = new Date().getUTCFullYear();

const AMPLITUDE_COMPONENT_NAME = "bills-overview";

interface Props {
  amplitudePageName: AmplitudePageName;
}

const BillsOverview: React.FC<Props> = ({ amplitudePageName }) => {
  const [billingData, setBillingData] = useState([] as any);
  const [, setError] = useState<any>();
  const [year, setYear] = useState(nowYear);

  const getBillingData = async (nextYear) => {
    try {
      const res = await queryBillingData(nextYear);
      setBillingData(res);
    } catch (err) {
      setError(err);
    }
  };

  // get billing data on init
  useEffect(() => {
    getBillingData(nowYear);
  }, []);

  const nextYear = (year) => {
    const nextYear = year + 1;
    setYear(nextYear);
    getBillingData(nextYear);
  };

  const prevYear = (year) => {
    const nextYear = year - 1;
    setYear(nextYear);
    getBillingData(nextYear);
  };

  return (
    <Row className={styles["bills-overview"]}>
      <Col className={styles["bills-overview__left"]}>
        <Row className="d-flex justify-content-between">
          <Col className="p-0 bd-16 gray01">Monthly Overview</Col>
          <Col className="p-0">
            <div className="d-flex justify-content-end mb-2">
              <div className="d-flex align-items-center">
                <IconButton
                  disabled={year <= 2020}
                  onClick={() => {
                    prevYear(year);
                    track(
                      amplitudePageName,
                      [AMPLITUDE_COMPONENT_NAME, "prev-year-button"],
                      "click",
                    );
                  }}
                >
                  <Previous />
                </IconButton>

                <div className="rg-14 gray01 mx-2">{year}</div>
                <IconButton
                  disabled={year >= nowYear}
                  onClick={() => {
                    nextYear(year);
                    track(
                      amplitudePageName,
                      [AMPLITUDE_COMPONENT_NAME, "next-year-button"],
                      "click",
                    );
                  }}
                >
                  <Next />
                </IconButton>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <div className="d-flex justify-content-between">
                <div className="d-flex align-items-center mr-3">
                  <BlueRectangle />
                  <div className="ml-1 rg-8 gray01">Receipt</div>
                </div>
                <div className="d-flex align-items-center">
                  <BrightGreenRectangle />
                  <div className="ml-1 rg-8 gray01">Superbill</div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <div className={styles["bills-overview__left-graphic"]}>
          <BarChart data={billingData} year={year} />
        </div>
      </Col>
      <Col className={styles["bills-overview__right"]}>
        <Table className={styles["bills-overview__right-table"]}>
          <thead>
            <tr className="bd-12">
              <th>Type</th>
              <th className="text-right pr-3">Total*</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Receipt</td>
              <td className="text-right pr-3">{billingData?.invoices_num}</td>
            </tr>
            <tr>
              <td>Superbill</td>
              <td className="text-right pr-3">{billingData?.superbills_num}</td>
            </tr>
          </tbody>
        </Table>
        <div className="d-flex">
          <div className="rg-12 gray01 mr-1">*</div>
          <div className="rg-12 gray01">
            A cumulative total numbers of bills created in {year}.
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default BillsOverview;
